import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BackToTop from './BackToTop';
import SEO from './SEO';
import { useTheme } from '../context/ThemeContext';

const ArticleDetailPage = () => {
  const { id } = useParams();
  const { isDarkMode } = useTheme();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const articleRef = useRef(null);

  const bgClass = isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]';
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#999999]' : 'text-[#555555]';
  const cardBgClass = isDarkMode ? 'bg-[#141414]' : 'bg-white';
  const borderClass = isDarkMode ? 'border-white/10' : 'border-[#E5E5E5]';

  // Fetch article data
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        // Fetch the article
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/${id}`);
        if (!response.ok) throw new Error('Article not found');
        const data = await response.json();
        
        setArticle({
          id: data.id,
          category: data.category,
          title: data.title,
          excerpt: data.excerpt,
          date: data.date,
          readTime: data.read_time,
          author: 'Flinch Team',
          color: data.color,
          heroImage: data.image_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80',
          content: data.content, // HTML content from API
        });

        // Fetch related articles
        const allResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights`);
        if (allResponse.ok) {
          const allData = await allResponse.json();
          setRelatedArticles(
            allData
              .filter(a => a.id !== id)
              .slice(0, 2)
              .map(a => ({
                id: a.id,
                category: a.category,
                title: a.title,
                date: a.date,
                readTime: a.read_time,
                color: a.color,
              }))
          );
        }
      } catch (err) {
        console.error('Error fetching article:', err);
        // Set a fallback article
        setArticle({
          id: id,
          category: 'Personal Branding',
          title: 'Article Not Found',
          excerpt: 'The requested article could not be loaded.',
          date: 'Dec 2024',
          readTime: '5 min read',
          author: 'Flinch Team',
          color: '#F6CD17',
          heroImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80',
          content: '<p>The article you are looking for could not be found. Please go back to insights and try again.</p>',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      if (articleRef.current) {
        const { top, height } = articleRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const progress = Math.min(Math.max((windowHeight - top) / (height + windowHeight), 0), 1);
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className={`min-h-screen ${bgClass} ${textClass} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F6CD17]"></div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-300`}>
      {/* SEO with Article Schema */}
      <SEO 
        title={article.title}
        description={article.excerpt || `Read "${article.title}" - expert insights on ${article.category} from FLINCH, your strategic marketing partner.`}
        canonical={`/insights/${article.id}`}
        image={article.heroImage}
        type="article"
        article={{
          title: article.title,
          excerpt: article.excerpt || `Read "${article.title}" - expert insights on ${article.category} from FLINCH.`,
          image: article.heroImage,
          datePublished: article.date,
          dateModified: article.date,
          category: article.category,
        }}
      />
      
      <Header />
      <BackToTop />
      
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50">
        <div 
          className="h-full transition-all duration-100"
          style={{ 
            width: `${scrollProgress * 100}%`,
            backgroundColor: article.color 
          }}
        />
      </div>

      {/* Hero Section */}
      <section className="pt-24 pb-8 px-6 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/insights" 
            className={`inline-flex items-center gap-2 ${subtextClass} hover:${textClass} transition-colors mb-8 group`}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="1.5"
              className="transition-transform group-hover:-translate-x-1"
            >
              <path d="M13 8H3M3 8L7 4M3 8L7 12" />
            </svg>
            Back to Insights
          </Link>

          {/* Category */}
          <span 
            className="inline-block text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-full mb-6"
            style={{ 
              backgroundColor: `${article.color}15`,
              color: article.color,
            }}
          >
            {article.category}
          </span>

          {/* Title */}
          <h1 className={`font-display text-4xl md:text-5xl lg:text-6xl ${textClass} mb-6 leading-[1.5] pt-1 pb-1`}>
            {article.title}
          </h1>

          {/* Meta */}
          <div className={`flex items-center gap-4 ${subtextClass} mb-8`}>
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="px-6 md:px-8 mb-12">
        <div className="max-w-5xl mx-auto">
          <img 
            src={article.heroImage} 
            alt={`${article.title} - Featured hero image for ${article.category} article`}
            className="w-full h-[40vh] md:h-[50vh] lg:h-[60vh] object-cover rounded-2xl"
          />
        </div>
      </section>

      {/* Article Content */}
      <article ref={articleRef} className="px-6 md:px-8 pb-20">
        <div className="max-w-3xl mx-auto">
          {/* Render HTML content from API */}
          <div 
            className={`prose prose-lg max-w-none ${isDarkMode ? 'prose-invert' : ''}`}
            style={{
              '--tw-prose-body': isDarkMode ? '#888888' : '#666666',
              '--tw-prose-headings': isDarkMode ? '#F2F2F2' : '#1A1A1A',
              '--tw-prose-lead': isDarkMode ? '#F2F2F2' : '#1A1A1A',
              '--tw-prose-links': article.color,
              '--tw-prose-bold': isDarkMode ? '#F2F2F2' : '#1A1A1A',
              '--tw-prose-quotes': isDarkMode ? '#F2F2F2' : '#1A1A1A',
              '--tw-prose-quote-borders': article.color,
            }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {/* Share Section */}
      <section className={`py-12 px-6 md:px-8 border-t ${borderClass}`}>
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className={subtextClass}>Share this article</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Twitter/X Share */}
            <a 
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-3 rounded-full border ${borderClass} hover:border-[#F6CD17] hover:bg-[#F6CD17]/10 transition-colors`}
              aria-label="Share on X (Twitter)"
              data-testid="share-twitter"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            {/* LinkedIn Share */}
            <a 
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-3 rounded-full border ${borderClass} hover:border-[#F6CD17] hover:bg-[#F6CD17]/10 transition-colors`}
              aria-label="Share on LinkedIn"
              data-testid="share-linkedin"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            {/* Copy Link */}
            <button 
              className={`p-3 rounded-full border ${borderClass} hover:border-[#F6CD17] hover:bg-[#F6CD17]/10 transition-colors`}
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
              }}
              aria-label="Copy link to clipboard"
              data-testid="share-copy"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section - Book a Call */}
      <section className={`py-16 px-6 md:px-8 ${isDarkMode ? 'bg-[#F6CD17]' : 'bg-[#F6CD17]'}`}>
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-display text-2xl md:text-3xl text-[#0A0A0A] mb-4">
            Ready to transform your marketing?
          </h3>
          <p className="text-[#0A0A0A]/70 mb-8 text-lg">
            Let's discuss how we can help you achieve your business goals with strategic, AI-enhanced marketing.
          </p>
          <a 
            href="mailto:contact@weareflinch.com?subject=Let's Talk - From Blog Article"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0A0A0A] text-white rounded-full font-medium hover:bg-[#1A1A1A] transition-all duration-300"
            data-testid="article-cta-button"
          >
            Book a Call
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 8H13M13 8L9 4M13 8L9 12" />
            </svg>
          </a>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className={`py-16 px-6 md:px-8 ${isDarkMode ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
          <div className="max-w-5xl mx-auto">
            <h3 className={`font-display text-2xl md:text-3xl ${textClass} mb-8`}>More Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedArticles.map(relatedArticle => (
                <Link
                  key={relatedArticle.id}
                  to={`/insights/${relatedArticle.id}`}
                  className={`group p-6 rounded-2xl border ${borderClass} ${cardBgClass} hover:border-[#F6CD17]/30 transition-all`}
                >
                  <span 
                    className="inline-block text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-full mb-4"
                    style={{ 
                      backgroundColor: `${relatedArticle.color}15`,
                      color: relatedArticle.color,
                    }}
                  >
                    {relatedArticle.category}
                  </span>
                  <h4 className={`font-display text-xl ${textClass} mb-2 group-hover:text-[#F6CD17] transition-colors`}>
                    {relatedArticle.title}
                  </h4>
                  <p className={`text-sm ${subtextClass}`}>
                    {relatedArticle.date} · {relatedArticle.readTime}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className={`py-16 px-6 md:px-8 ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#1A1A1A]'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-display text-2xl md:text-3xl text-white mb-4">
            Want to work together?
          </h3>
          <p className="text-white/60 mb-8">
            Let's talk about how Flinch can help build your brand.
          </p>
          <a 
            href="mailto:contact@weareflinch.com" 
            className="glass-button-light inline-flex items-center gap-2"
          >
            Get in touch
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 8H13M13 8L9 4M13 8L9 12" />
            </svg>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticleDetailPage;
